// ** React Imports
import { useEffect, useState } from 'react'

// ** MUI Imports
import Drawer from '@mui/material/Drawer'
import Select from '@mui/material/Select'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import { styled } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import InputLabel from '@mui/material/InputLabel'
import Typography from '@mui/material/Typography'
import Box, { BoxProps } from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'


// ** Third Party Imports
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, Controller } from 'react-hook-form'

// ** Icons Imports
import Close from 'mdi-material-ui/Close'

// ** Store Imports
import { client } from 'src/utils/axios-client';
import { SelectOption } from 'src/types/common';
import CustomDatePicker from '../common/CustomDatePicker'
import { Customer } from '../../stores/CustomerStore'
import { ApiResponseType } from '../../types'
import ApplicationStore from '../../stores/ApplicationStore'
import { saveCustomerEndpoint } from '../../configs/endpoints'
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import CloseIcon from '@mui/icons-material/Close';
import SimpleDialog from '../common/SimpleDialog'
import SectionAppBar from '../../v2/components/Section/SectionAppBar'
import SaveButton from '../../v2/components/Button/SaveButton'
import StandardButton from '../../v2/components/Button/StandardButton'
import FormWithLoader from '../../v2/components/FormWithLoader/FormWithLoader'

interface Props {
    open: boolean
    toggle: () => void,
    customer: Customer
}

const Header = styled(Box)<BoxProps>(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(3, 4),
    justifyContent: 'space-between',
    backgroundColor: theme.palette.background.default
}))

const schema = yup.object().shape({
    name: yup.string().required()
})

const EditCustomerDrawer = ({ open, toggle, customer }: Props) => {
    const {
        reset,
        control,
        setValue,
        handleSubmit,
        formState: { errors }
    } = useForm({
        defaultValues: customer,
        mode: 'onChange',
        resolver: yupResolver(schema)
    })

    useEffect(() => {
        reset(customer);
    }, [customer])

    const onSubmit = async (data: Customer) => {
        try {
            const response = await client.post<ApiResponseType>(saveCustomerEndpoint, data)

            if (response.data.succeeded) {
                ApplicationStore.update(s => {
                    s.snackbarMessage = 'Record saved';
                    s.snackbarOpen = true;
                })

                handleClose();
            } else {
                ApplicationStore.update(s => {
                    s.snackbarMessage = 'Error saving record ' + response.data.errors[0].code;
                    s.snackbarOpen = true;
                })
            }
        } catch (err) {
            ApplicationStore.update(s => {
                s.snackbarMessage = 'Error saving record ' + err.errors[0].code;
                s.snackbarOpen = true;
            })
        }
    }

    const handleClose = () => {
        toggle()
    }

    return (
        <SimpleDialog
            open={open}
            onClose={handleClose}
            padding={0}
            fullHeight={false}
            size='sm'
        >
            <SectionAppBar
                title={`Edit Customer`}
                subTitle={''}
                buttons={
                    <>
                        <SaveButton size='small' type='submit' variant='contained' onClick={handleSubmit(onSubmit)} icon={<SaveAltIcon />}>
                            Save
                        </SaveButton>
                        <StandardButton size='small' variant='outlined' color='secondary' onClick={handleClose} icon={<CloseIcon />}>
                            Cancel
                        </StandardButton>
                    </>
                }
            />
            <Box sx={{ p: 5 }}>
                <FormWithLoader onSubmit={handleSubmit(onSubmit)}>


                    <FormControl fullWidth sx={{ mb: 6 }}>
                        <Controller
                            name='name'
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { value, onChange } }) => (
                                <TextField
                                    size='small'
                                    value={value}
                                    label='Name'
                                    onChange={onChange}
                                    placeholder='Customer Name'
                                    error={Boolean(errors.name)}
                                />
                            )}
                        />
                        {errors.name && <FormHelperText sx={{ color: 'error.main' }}>{errors.name.message}</FormHelperText>}
                    </FormControl>
                </FormWithLoader>
            </Box>
        </SimpleDialog>
    )
}

export default EditCustomerDrawer
